import Dexie, { type Table } from 'dexie';
import type { Mutation, StoreCode, AdapterHealth } from '@familyhub/shared';

/**
 * The local database (ARCHITECTURE §14.1).
 *
 * IndexedDB via Dexie is **the one place this client keeps durable state**.
 * Nothing may drift into `localStorage`: it is synchronous, size-limited, and
 * would give the outbox a second home that the replay logic does not know
 * about.
 *
 * The tables mirror the sync set (DATABASE §12) plus three local-only ones:
 *
 *   - `outbox`   — queued mutations, replayed **in order** (§11).
 *   - `prices`   — shelf prices are not in the sync set, so they are cached
 *                  here with their `fetched_at` and rendered as stale when old.
 *                  Never presenting an old price as current is the rule; hiding
 *                  the price entirely when offline would be worse.
 *   - `meta`     — the pull cursor, the device id, the signed-in user.
 *
 * Rows are stored exactly as the server sends them, tombstones included: a
 * `deleted_at` row is what makes a delete propagate instead of being
 * resurrected, so it is kept and filtered at read time, never deleted here.
 */

export interface ListItemRecord {
  id: string;
  store_id: string | null;
  generic_product_id: string;
  variant_id: string | null;
  quantity: number;
  note: string | null;
  added_by_user: string;
  suggestion_reason: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface GenericProductRecord {
  id: string;
  name: string;
  slug: string;
  category: string | null;
  category_source: string;
  /**
   * Whether this is an organic product (migration 019). Server-derived and
   * mirrored here so the "Biologisch" filter works with no signal. Absent on
   * rows cached before the feature shipped, so read it as `!!organic`.
   */
  organic?: boolean;
  updated_at: string;
  deleted_at: string | null;
}

export interface ProductVariantRecord {
  id: string;
  generic_product_id: string;
  store_id: string;
  name: string;
  /**
   * The model's short label for this variant — "Oatly Barista" (migration
   * 016). Null until the classification sweep has reached the row, which is
   * why every screen goes through `variantLabel()` rather than reading this
   * directly.
   */
  short_name: string | null;
  brand: string | null;
  size_value: number | null;
  size_unit: string | null;
  ean: string | null;
  external_id: string | null;
  /** The shop's own URL. Stored as it arrives; never rendered — see `image_path`. */
  image_url: string | null;
  /**
   * Same-origin path the API serves the product photograph from
   * (`/api/v1/images/<hash>`). Server-emitted, never sent back.
   *
   * This is the field a screen renders. Pointing an <img> at `image_url`
   * instead would reach a supermarket's CDN — which fails in a shop with no
   * signal, needs four CDN hosts added to the CSP, and tells four retailers
   * what is on this household's list.
   */
  image_path: string | null;
  /**
   * Whether this is an organic product (migration 019). Server-derived from the
   * store, title and brand and mirrored here so the "Biologisch" filter works
   * offline. Absent on rows cached before the feature shipped — read `!!organic`.
   */
  organic?: boolean;
  /**
   * Hidden from "Jullie producten" (migration 041) — a reversible flag, not a
   * delete, so list items and history that point at it are untouched. Absent on
   * rows cached before it shipped: read `!!hidden`.
   */
  hidden?: boolean;
  /** Pulled with the row; carried back unchanged when this phone writes it. */
  classification_source?: 'auto' | 'confirmed' | 'manual';
  cross_store_product_id?: string | null;
  updated_at: string;
  deleted_at: string | null;
}

export interface CategoryOrderRecord {
  /** Dexie needs a single primary key; the compound is joined for it. */
  key: string;
  store_id: string;
  category: string;
  position: number;
  updated_at: string;
}

export interface StoreRecord {
  id: string;
  code: StoreCode;
  name: string;
  supports_search: boolean;
  health: AdapterHealth;
  health_note: string | null;
}

/** The price overlay: server data, cached locally so a shop with no signal still shows it. */
export interface PriceRecord {
  variant_id: string;
  price_cents: number | null;
  fetched_at: string | null;
}

/**
 * Suggestions, cached from the same `GET /list` that carries shelf prices.
 *
 * They are ranked server-side from purchase history, which a phone does not
 * hold in full — but they were also *fetched* separately by the list
 * screen, so every render made the same request twice and produced nothing at
 * all with no signal. Planning a shop is when suggestions are most useful and
 * is exactly when somebody is sitting at the kitchen table on bad wifi.
 */
export interface SuggestionRecord {
  generic_product_id: string;
  name: string;
  reason: string;
  confidence: number;
  suggested_variant_id: string | null;
  fetched_at: string;
}

/**
 * The last comparison the household saw for a product.
 *
 * Comparing is inherently an online question — it asks what every shop charges
 * right now — but it is asked in a shop, which is where there is no reception.
 * Showing an error there is honest and useless; showing the last answer with
 * its age is honest and usable, and it is the same bargain the rest of the app
 * already makes with shelf prices.
 *
 * **Never presented as current.** Everything from here renders stale, with the
 * date it was fetched.
 */
export interface CompareCacheRecord {
  generic_product_id: string;
  response: unknown;
  fetched_at: string;
}

/**
 * The purchase-history overlay: server-derived, cached like shelf prices.
 *
 * API §4.1 asks local search to be ranked by purchase history **and** to work
 * offline. Purchases are append-only and not in the sync set, so without this
 * a phone with no signal could only sort alphabetically — burying the thing the
 * household buys every week under a lexical near-match.
 */
export interface HistoryRecord {
  generic_product_id: string;
  purchase_count: number;
  last_purchased_at: string | null;
  /** The product a re-add defaults to; absent on rows cached before it existed. */
  usual_variant_id?: string | null;
}

export interface OutboxEntry {
  /** Auto-incremented, and the reason replay order is preserved across restarts. */
  seq?: number;
  mutation: Mutation;
  queued_at: string;
}

/**
 * A mutation the server refused.
 *
 * Rejections are validation failures, never conflicts (API §3.2), so most will
 * not fix themselves on a retry — they are kept and shown rather than dropped
 * silently (principle 6).
 *
 * **The mutation is kept with them.** Without it a rejection is a code and a
 * sentence about something the person can no longer identify, which is worse
 * than useless: they know they lost a change but not which one. With it the app
 * can name what failed, and retry the one case that genuinely resolves itself —
 * a reference to a row that had not synced yet.
 */
export interface RejectionRecord {
  id: string;
  code: string;
  message: string;
  at: string;
  mutation: Mutation;
  attempts: number;
}

export interface MetaRecord {
  key: string;
  value: unknown;
}

export class FamilyHubDb extends Dexie {
  list_items!: Table<ListItemRecord, string>;
  generic_products!: Table<GenericProductRecord, string>;
  product_variants!: Table<ProductVariantRecord, string>;
  store_category_order!: Table<CategoryOrderRecord, string>;
  stores!: Table<StoreRecord, string>;
  prices!: Table<PriceRecord, string>;
  history!: Table<HistoryRecord, string>;
  compare_cache!: Table<CompareCacheRecord, string>;
  suggestions!: Table<SuggestionRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  rejections!: Table<RejectionRecord, string>;
  meta!: Table<MetaRecord, string>;

  constructor() {
    super('familyhub');
    this.version(1).stores({
      // Only indexed fields are listed; the whole object is still stored.
      list_items: 'id, store_id, generic_product_id, updated_at, deleted_at',
      generic_products: 'id, slug, category, updated_at',
      product_variants: 'id, generic_product_id, store_id, external_id',
      store_category_order: 'key, store_id, position',
      stores: 'id, code',
      prices: 'variant_id',
      history: 'generic_product_id, purchase_count',
      compare_cache: 'generic_product_id, fetched_at',
      suggestions: 'generic_product_id, confidence',
      outbox: '++seq, queued_at',
      pending_uploads: 'id, queued_at',
      rejections: 'id, at',
      meta: 'key',
    });
    /**
     * `pending_uploads` held receipt photographs waiting for signal, and went
     * with the receipt feature (migration 026). Deleted explicitly rather than
     * simply dropped from the schema above: Dexie diffs each version against
     * the last, so omitting a store *keeps* it, and an installed phone would
     * carry the photos and their bytes forever with nothing able to read them.
     */
    this.version(2).stores({ pending_uploads: null });
  }
}

export const db = new FamilyHubDb();

export const categoryOrderKey = (storeId: string, category: string) => `${storeId}|${category}`;

/* ---------------- meta helpers ---------------- */

/**
 * The shops, in the order they should always appear.
 *
 * `db.stores.toArray()` returns them in primary-key order and the key is a
 * uuid, so every screen that read it directly got its own arbitrary order —
 * and the arbitrary order changes with the deployment. Five screens offer the
 * same six buttons to a thumb that reaches for them without looking; they
 * belong in the same places every time and in the same places everywhere.
 */
export async function storesByName(): Promise<StoreRecord[]> {
  return (await db.stores.toArray()).sort((a, b) => a.name.localeCompare(b.name));
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/**
 * API §1: every request carries `X-Device-Id`, and the server stamps
 * `updated_by_device` from it. Generated once per install and kept in
 * IndexedDB with everything else — a device id in localStorage would be the
 * exact drift the module comment warns about.
 */
export async function deviceId(): Promise<string> {
  const existing = await getMeta<string>('device_id');
  if (existing) return existing;
  const created = crypto.randomUUID();
  await setMeta('device_id', created);
  return created;
}

/** Wipe everything on logout: a shared phone must not keep the other household's list. */
export async function clearLocalData(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.list_items,
      db.generic_products,
      db.product_variants,
      db.store_category_order,
      db.stores,
      db.prices,
      db.history,
      db.compare_cache,
      db.suggestions,
      db.outbox,
      db.rejections,
      db.meta,
    ],
    async () => {
      const keepDeviceId = await getMeta<string>('device_id');
      await Promise.all([
        db.list_items.clear(),
        db.generic_products.clear(),
        db.product_variants.clear(),
        db.store_category_order.clear(),
        db.stores.clear(),
        db.prices.clear(),
        db.history.clear(),
        db.compare_cache.clear(),
        db.suggestions.clear(),
        db.outbox.clear(),
        db.rejections.clear(),
        db.meta.clear(),
      ]);
      // The device keeps its identity across sign-ins; it is the same phone.
      if (keepDeviceId) await db.meta.put({ key: 'device_id', value: keepDeviceId });
    },
  );
}
